import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  fetchAllCommunities,
  fetchCommunityById,
  createCommunity,
  updateCommunity,
  addRCRProjectManager,
  fetchRCRProjectManagersByCommunit,
  fetchAllUsersGlobal,
  fetchUsersByCommunity,
  updateUserRole,
  deleteUserGlobal,
  fetchAllPropertiesGlobal,
  fetchPropertiesByCommunity,
  fetchDashboardStats,
} from "../api/superAdminApi";

// ===== COMMUNITY HOOKS =====
export const useCommunities = () => {
  const queryClient = useQueryClient();

  const {
    data: communities,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["communities"],
    queryFn: fetchAllCommunities,
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to fetch communities"
      );
    },
  });

  const createCommunityMutation = useMutation({
    mutationFn: createCommunity,
    onSuccess: (data) => {
      toast.success(data.message || "Community created successfully!");
      queryClient.invalidateQueries(["communities"]);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create community"
      );
    },
  });

  const updateCommunityMutation = useMutation({
    mutationFn: ({ id, data }) => updateCommunity(id, data),
    onSuccess: (data) => {
      toast.success(data.message || "Community updated successfully!");
      queryClient.invalidateQueries(["communities"]);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to update community"
      );
    },
  });

  return {
    communities: communities || [],
    isLoading,
    error,
    createCommunity: createCommunityMutation.mutate,
    updateCommunity: updateCommunityMutation.mutate,
    createCommunityLoading: createCommunityMutation.isPending,
  };
};

// ===== RCR PROJECT MANAGER HOOKS =====
export const useRCRProjectManagers = (communityId) => {
  const queryClient = useQueryClient();

  const {
    data: managers,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["rcrProjectManagers", communityId],
    queryFn: () => fetchRCRProjectManagersByCommunit(communityId),
    enabled: !!communityId,
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to fetch RCR Project Managers"
      );
    },
  });

  const addManagerMutation = useMutation({
    mutationFn: addRCRProjectManager,
    onSuccess: (data) => {
      toast.success(data.message || "RCR Project Manager added successfully!");
      queryClient.invalidateQueries(["rcrProjectManagers"]);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to add RCR Project Manager"
      );
    },
  });

  return {
    managers: managers || [],
    isLoading,
    error,
    addManager: addManagerMutation.mutate,
    addManagerLoading: addManagerMutation.isPending,
  };
};

// ===== GLOBAL USER MANAGEMENT HOOKS =====
export const useGlobalUsers = () => {
  const {
    data: users,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["globalUsers"],
    queryFn: fetchAllUsersGlobal,
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to fetch users");
    },
  });

  return {
    users: users || [],
    isLoading,
    error,
  };
};

export const useCommunityUsers = (communityId) => {
  const queryClient = useQueryClient();

  const {
    data: users,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["communityUsers", communityId],
    queryFn: () => fetchUsersByCommunity(communityId),
    enabled: !!communityId,
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to fetch users");
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }) => updateUserRole(userId, role),
    onSuccess: (data) => {
      toast.success(data.message || "User role updated successfully!");
      queryClient.invalidateQueries(["communityUsers"]);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to update user role"
      );
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: deleteUserGlobal,
    onSuccess: (data) => {
      toast.success(data.message || "User deleted successfully!");
      queryClient.invalidateQueries(["communityUsers"]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete user");
    },
  });

  return {
    users: users || [],
    isLoading,
    error,
    updateRole: updateRoleMutation.mutate,
    deleteUser: deleteUserMutation.mutate,
    updateRoleLoading: updateRoleMutation.isPending,
    deleteUserLoading: deleteUserMutation.isPending,
  };
};

// ===== GLOBAL PROPERTY MANAGEMENT HOOKS =====
export const useGlobalProperties = () => {
  const {
    data: properties,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["globalProperties"],
    queryFn: fetchAllPropertiesGlobal,
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to fetch properties"
      );
    },
  });

  return {
    properties: properties || [],
    isLoading,
    error,
  };
};

export const useCommunityProperties = (communityId) => {
  const {
    data: properties,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["communityProperties", communityId],
    queryFn: () => fetchPropertiesByCommunity(communityId),
    enabled: !!communityId,
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to fetch properties"
      );
    },
  });

  return {
    properties: properties || [],
    isLoading,
    error,
  };
};

// ===== DASHBOARD HOOKS =====
export const useDashboardStats = () => {
  const {
    data: stats,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["dashboardStats"],
    queryFn: fetchDashboardStats,
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to fetch dashboard stats"
      );
    },
  });

  return {
    stats,
    isLoading,
    error,
  };
};
